import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../lib/api'
import ActivityFilters from '../components/activities/ActivityFilters'
import ActivityCard from '../components/activities/ActivityCard'
import Button from '../components/ui/Button'
import { getFallbackActivities, getGradeTopics } from '../data/bnccContent'

const supportOptions = [
  { value: 'all', label: 'Todos os perfis' },
  { value: 'TEA', label: 'TEA' },
  { value: 'TDAH', label: 'TDAH' },
  { value: 'TOC', label: 'TOC' },
  { value: 'Down', label: 'Síndrome de Down' },
  { value: 'outros', label: 'Outros perfis' }
]

const dimensionOptions = [
  { value: 'all', label: '4 dimensões' },
  { value: 'ensino-regular', label: 'Ensino regular' },
  { value: 'adaptado', label: 'Ensino adaptado' },
  { value: 'programacao', label: 'Programação em blocos' },
  { value: 'games', label: 'Games em Python' }
]

const dimensionMap = {
  'ensino-regular': 'regular',
  adapted: 'adapted',
  adaptado: 'adapted',
  programacao: 'blocks',
  blocks: 'blocks',
  games: 'games'
}

function normalizeText(value = '') {
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

function matchesTopic(activity, selectedTopic) {
  if (selectedTopic === 'all') return true
  const haystack = normalizeText([
    activity.topic,
    activity.title,
    activity.description,
    ...(activity.tags || [])
  ].filter(Boolean).join(' '))
  return haystack.includes(normalizeText(selectedTopic))
}

function applyLocalFilters(items, filter, supportNeed, dimension, selectedGrade, selectedTopic) {
  return items.filter((activity) => {
    const okType = filter === 'all' || activity.type === filter
    const okSupport = supportNeed === 'all' || (activity.supportNeeds || []).includes(supportNeed)
    const okDimension = dimension === 'all' || (activity.learningDimensions || []).includes(dimension)
    const okGrade = Number(activity.grade) === Number(selectedGrade)
    const okTopic = matchesTopic(activity, selectedTopic)
    return okType && okSupport && okDimension && okGrade && okTopic
  })
}

export default function ActivitiesPage() {
  const navigate = useNavigate()
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [supportNeed, setSupportNeed] = useState('all')
  const [dimension, setDimension] = useState('all')
  const [selectedGrade, setSelectedGrade] = useState(1)
  const [selectedTopic, setSelectedTopic] = useState('all')
  const [visibleCount, setVisibleCount] = useState(10)
  const [message, setMessage] = useState('')
  const fallbackActivities = useMemo(() => getFallbackActivities(), [])
  const topics = useMemo(() => getGradeTopics(selectedGrade).map((item) => item.title), [selectedGrade])
  const gradeOptions = useMemo(() => Array.from({ length: 9 }, (_, index) => ({ value: index + 1, label: `${index + 1}º ano` })), [])

  useEffect(() => {
    fetchActivities()
  }, [filter, supportNeed, dimension, selectedGrade, selectedTopic])

  useEffect(() => {
    setVisibleCount(10)
  }, [filter, supportNeed, dimension, selectedGrade, selectedTopic])

  useEffect(() => {
    if (selectedTopic !== 'all' && !topics.includes(selectedTopic)) {
      setSelectedTopic('all')
    }
  }, [topics, selectedTopic])

  const fetchActivities = async () => {
    try {
      setLoading(true)
      const params = { grade: selectedGrade, limit: 200 }
      if (filter !== 'all') params.type = filter
      if (supportNeed !== 'all') params.supportNeed = supportNeed
      if (dimension !== 'all') params.dimension = dimension
      if (selectedTopic !== 'all') params.topic = selectedTopic
      const response = await api.get('/activities', { params })
      const merged = [...(response.data.activities || [])]
      const seen = new Set(merged.map((item) => String(item.id)))
      fallbackActivities.forEach((item) => { if (!seen.has(String(item.id))) merged.push(item) })
      setActivities(applyLocalFilters(merged, filter, supportNeed, dimension, selectedGrade, selectedTopic))
      setMessage('')
    } catch (error) {
      setActivities(applyLocalFilters(fallbackActivities, filter, supportNeed, dimension, selectedGrade, selectedTopic))
      setMessage('Atividades BNCC carregadas em modo local enquanto a API não responde.')
    } finally {
      setLoading(false)
    }
  }

  const handleStart = async (activity) => {
    const normalizedDimension = activity.dimension || dimensionMap[activity.learningDimensions?.[0]] || (activity.type === 'game' ? 'games' : activity.type === 'blockly' ? 'blocks' : 'regular')
    const params = new URLSearchParams({
      grade: String(activity.grade || selectedGrade || 1),
      topic: activity.topic || activity.title || selectedTopic,
      dimension: normalizedDimension,
      activityId: String(activity.id),
      support: supportNeed === 'all' ? 'geral' : supportNeed
    })
    const token = localStorage.getItem('token')
    if (token) {
      const score = Math.min(100, activity.estimatedScore || 100)
      const timeSpent = (activity.durationMinutes || 5) * 60
      try {
        await api.post(`/activities/${activity.id}/complete`, { score, timeSpent })
        setMessage(`Atividade "${activity.title}" registrada com sucesso e aberta no estúdio.`)
      } catch (error) {
        setMessage('Atividade aberta no estúdio. O registro de progresso ficou para a próxima tentativa.')
      }
    } else {
      setMessage(`Abrindo "${activity.title}" no estúdio. Entre com login para registrar progresso e créditos.`)
    }
    navigate(`/estudio?${params.toString()}`)
  }

  const visibleActivities = activities.slice(0, visibleCount)
  const canLoadMore = visibleCount < activities.length

  return (
    <div className="page-stack">
      <section className="section-title-wrap">
        <span className="eyebrow">Trilhas adaptadas</span>
        <h1>Atividades de matemática por série, BNCC e mediação inclusiva.</h1>
        <p>Escolha a série e o assunto BNCC. As atividades surgem em blocos de 10 e o professor pode ampliar sempre mais 10 exercícios por vez.</p>
      </section>

      {message ? <div className="alert info">{message}</div> : null}

      <ActivityFilters
        filter={filter}
        setFilter={setFilter}
        supportNeed={supportNeed}
        setSupportNeed={setSupportNeed}
        dimension={dimension}
        setDimension={setDimension}
        supportOptions={supportOptions}
        dimensionOptions={dimensionOptions}
        selectedGrade={selectedGrade}
        setSelectedGrade={setSelectedGrade}
        gradeOptions={gradeOptions}
        selectedTopic={selectedTopic}
        setSelectedTopic={setSelectedTopic}
        topicOptions={topics}
      />

      <div className="surface-card compact">
        <strong>{selectedGrade}º ano</strong> • {selectedTopic === 'all' ? 'Todos os assuntos BNCC' : selectedTopic} • exibindo {Math.min(visibleCount, activities.length)} de {activities.length} atividades
      </div>

      {loading ? <div className="loading-box">Carregando atividades...</div> : null}

      {!loading ? (
        <div className="grid cols-3">
          {visibleActivities.map((activity) => (
            <ActivityCard key={activity.id} activity={activity} onStart={handleStart} />
          ))}
        </div>
      ) : null}

      {!loading && canLoadMore ? (
        <div className="button-wrap">
          <Button onClick={() => setVisibleCount((current) => current + 10)}>Mostrar mais 10 atividades</Button>
        </div>
      ) : null}

      {!loading && activities.length === 0 ? (
        <div className="surface-card">Nenhuma atividade encontrada para a série, assunto BNCC e filtros escolhidos.</div>
      ) : null}
    </div>
  )
}
